import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useAppDispatch } from "../app/hooks"
import { PRICEING_RULES } from "../constants/routeConstants"
import { DmsTypeEnum } from "../constants/enums"
import { logout, setCredentials, setDmsType } from "../features/auth/authSlice"
import { decodeToken, getProfileFromToken } from "../helpers/helpers"
import { useLazyValidateTokenQuery } from "../services/API/accountsAPI"

export const useValidateAccessTokenFromUrl = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [validateToken] = useLazyValidateTokenQuery()
  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const lastValidatedTokenRef = useRef<string | null>(null)

  const getDealerDmsType = async (token: string, dealerGuid: string) => {
    try {
      const EVEREST_BASE_URL = import.meta.env.VITE_EVEREST_BASE_URL
      const apiUrl = `${EVEREST_BASE_URL}Dealers/v1/${dealerGuid}`

      const res = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (!res.ok) throw new Error(`EverestAPI error ${res.status}`)
      const data = await res.json()

      const apiDmsType = (data?.dmsType ?? data?.DmsType) as
        | number
        | string
        | undefined

      let dmsTypeValue: number | undefined
      if (typeof apiDmsType === "string") {
        const enumName = apiDmsType.trim()
        const enumCandidate = (DmsTypeEnum as any)[enumName]
        if (typeof enumCandidate === "number") {
          dmsTypeValue = enumCandidate
        }
      } else if (typeof apiDmsType === "number") {
        if ((DmsTypeEnum as any)[apiDmsType] !== undefined) {
          dmsTypeValue = apiDmsType
        }
      }

      if (dmsTypeValue !== undefined && !Number.isNaN(dmsTypeValue)) {
        dispatch(setDmsType({ dmsType: dmsTypeValue }))
      } else {
        console.warn("EverestAPI: dmsType missing in response")
      }
    } catch (err) {
      console.error("Failed to fetch DmsType from EverestAPI:", err)
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const accessToken = searchParams.get("access_token")

    if (!accessToken) return
    if (lastValidatedTokenRef.current === accessToken) return

    lastValidatedTokenRef.current = accessToken

    setIsAuthorizing(true)

    const validateAccessToken = async () => {
      try {
        const result = await validateToken(accessToken).unwrap()
        const isValid = result?.isValid ?? result?.IsValid

        if (!isValid) {
          dispatch(logout())
          toast.error("Invalid or expired token")
          navigate("/login", { replace: true })
          return
        }

        const decoded = decodeToken(accessToken)
        const profile = getProfileFromToken(accessToken)
        const expiresIn = Math.max(
          decoded.exp - Math.floor(Date.now() / 1000),
          0,
        )

        dispatch(
          setCredentials({
            access_token: accessToken,
            expires_in: expiresIn,
            refresh_token: "",
            token_type: "Bearer",
            scope: "",
            username: decoded.name,
            dealerId: profile?.dealerId,
            dmsDealerId: profile?.everestDmsDealerId,
            companyDealerId: profile?.companyDealerId,
            companyName: profile?.companyName,
            dealerGuid: profile?.dealerGuid,
          }),
        )

        if (profile?.dealerGuid) {
          await getDealerDmsType(accessToken, profile.dealerGuid)
        }

        searchParams.delete("access_token")
        const updatedSearch = searchParams.toString()
        const nextPathname =
          location.pathname === "/login" ? PRICEING_RULES : location.pathname

        navigate(
          {
            pathname: nextPathname,
            search: updatedSearch ? `?${updatedSearch}` : "",
          },
          { replace: true },
        )
      } catch (error) {
        dispatch(logout())
        toast.error("Token validation failed")
        navigate("/login", { replace: true })
      } finally {
        setIsAuthorizing(false)
      }
    }

    void validateAccessToken()
  }, [dispatch, location.pathname, location.search, navigate, validateToken])

  return { isAuthorizing }
}
